import { DeviceSettings } from '../types';

export class SettingsModal {
  private settings: DeviceSettings = {
    audioInputId: '',
    audioOutputId: '',
    videoInputId: '',
    videoQuality: '720p',
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
    audioOnly: false,
    mirrored: true,
  };

  public onSettingsChanged?: (newSettings: DeviceSettings) => void;
  public onClose?: () => void;

  public async render(container: HTMLElement) {
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);

    const audioInputs = devices.filter(d => d.kind === 'audioinput');
    const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
    const videoInputs = devices.filter(d => d.kind === 'videoinput');

    container.innerHTML = `
      <div class="modal-backdrop" id="settings-backdrop">
        <div class="modal-content">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--border-subtle); padding-bottom: 12px;">
            <h3>Device & Media Settings</h3>
            <button id="btn-close-modal" class="control-btn" style="width: 32px; height: 32px;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div style="display: flex; flex-direction: column; gap: 14px;">
            <!-- Camera Selection -->
            <div class="form-group">
              <label class="form-label">Camera</label>
              <select id="settings-camera-select" class="form-input">
                <option value="">Default System Camera</option>
                ${videoInputs.map(v => `<option value="${v.deviceId}" ${this.settings.videoInputId === v.deviceId ? 'selected' : ''}>${v.label || 'Camera'}</option>`).join('')}
              </select>
            </div>

            <!-- Microphone Selection -->
            <div class="form-group">
              <label class="form-label">Microphone</label>
              <select id="settings-mic-select" class="form-input">
                <option value="">Default System Microphone</option>
                ${audioInputs.map(a => `<option value="${a.deviceId}" ${this.settings.audioInputId === a.deviceId ? 'selected' : ''}>${a.label || 'Microphone'}</option>`).join('')}
              </select>
            </div>

            <!-- Speaker Selection -->
            ${audioOutputs.length > 0 ? `
              <div class="form-group">
                <label class="form-label">Speaker / Audio Output</label>
                <select id="settings-speaker-select" class="form-input">
                  <option value="">Default System Speaker</option>
                  ${audioOutputs.map(o => `<option value="${o.deviceId}" ${this.settings.audioOutputId === o.deviceId ? 'selected' : ''}>${o.label || 'Speaker'}</option>`).join('')}
                </select>
              </div>
            ` : ''}

            <!-- Toggles -->
            <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 4px;">
              <label class="form-checkbox">
                <input type="checkbox" id="check-noise-suppression" ${this.settings.noiseSuppression ? 'checked' : ''} style="accent-color: var(--brand-primary);" />
                <span>AI Noise Suppression & Echo Cancellation</span>
              </label>

              <label class="form-checkbox">
                <input type="checkbox" id="check-mirror-video" ${this.settings.mirrored ? 'checked' : ''} style="accent-color: var(--brand-primary);" />
                <span>Mirror my local video</span>
              </label>

              <label class="form-checkbox">
                <input type="checkbox" id="check-audio-only" ${this.settings.audioOnly ? 'checked' : ''} style="accent-color: var(--color-warning);" />
                <span>Low Bandwidth Mode (Audio only, disable incoming video)</span>
              </label>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px;">
            <button id="btn-save-settings" class="btn-primary" style="width: auto; padding: 10px 24px;">
              Save Settings
            </button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents(container);
  }

  private bindEvents(container: HTMLElement) {
    const backdrop = container.querySelector('#settings-backdrop');
    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        container.innerHTML = '';
        if (this.onClose) this.onClose();
      }
    });

    container.querySelector('#btn-close-modal')?.addEventListener('click', () => {
      container.innerHTML = '';
      if (this.onClose) this.onClose();
    });

    container.querySelector('#btn-save-settings')?.addEventListener('click', () => {
      const cam = container.querySelector('#settings-camera-select') as HTMLSelectElement;
      const mic = container.querySelector('#settings-mic-select') as HTMLSelectElement;
      const speaker = container.querySelector('#settings-speaker-select') as HTMLSelectElement;
      const noise = container.querySelector('#check-noise-suppression') as HTMLInputElement;
      const mirror = container.querySelector('#check-mirror-video') as HTMLInputElement;
      const audioOnly = container.querySelector('#check-audio-only') as HTMLInputElement;

      this.settings = {
        ...this.settings,
        videoInputId: cam?.value || '',
        audioInputId: mic?.value || '',
        audioOutputId: speaker?.value || '',
        noiseSuppression: noise?.checked || false,
        mirrored: mirror?.checked || false,
        audioOnly: audioOnly?.checked || false,
      };

      if (this.onSettingsChanged) {
        this.onSettingsChanged(this.settings);
      }

      container.innerHTML = '';
      if (this.onClose) this.onClose();
    });
  }
}
